import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { clientAPI } from '../../services/api';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';

// Sub-components
import AgentHeader from '../../components/agent-editor/AgentHeader';
import DetailsTab from '../../components/agent-editor/tabs/DetailsTab';
import CallConfigTab from '../../components/agent-editor/tabs/CallConfigTab';
import KnowledgeBaseTab from '../../components/agent-editor/tabs/KnowledgeBaseTab';
import IntegrationsTab from '../../components/agent-editor/tabs/IntegrationsTab';
import PostCallTab from '../../components/agent-editor/tabs/PostCallTab';
import RecentCallsTab from '../../components/agent-editor/tabs/RecentCallsTab';
import VersionHistoryTab from '../../components/agent-editor/tabs/VersionHistoryTab';

// Constants
import { defaultAgentConfig } from '../../constants/agentConfig';

// Auto-save debounce delay in milliseconds
const AUTO_SAVE_DELAY = 2000;

const AgentEditor = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { clientUser, clientSettings } = useAuth();
  const isNewAgent = agentId === 'new';

  // UI State
  const [activeTab, setActiveTab] = useState('details');
  const [viewMode, setViewMode] = useState('ui');
  const [showTestDropdown, setShowTestDropdown] = useState(false);
  const [showDeployDropdown, setShowDeployDropdown] = useState(false);
  const [loading, setLoading] = useState(!isNewAgent);
  const [saving, setSaving] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [notification, setNotification] = useState(null);
  
  // Auto-save state
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved' | 'unsaved' | 'saving' | 'error'
  const [lastSaved, setLastSaved] = useState(null);
  const autoSaveTimerRef = useRef(null);
  const initialLoadRef = useRef(true);

  // Agent State
  const [agent, setAgent] = useState({ ...defaultAgentConfig });

  // Auto-hide notification
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.relative')) {
        setShowTestDropdown(false);
        setShowDeployDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Fetch agent data
  const fetchAgent = useCallback(async () => {
    if (isNewAgent || !clientUser?.id) return;
    
    try {
      const response = await clientAPI.getAgent(clientUser?.id, agentId);
      
      if (response.data.agent) {
        const data = response.data.agent;
        setAgent({
          ...defaultAgentConfig,
          ...data,
          voiceProvider: data.voice_provider || 'google',
          voiceId: data.voice_id || 'despina',
          voiceGender: data.voice_gender || 'female',
          voicePitch: data.voice_pitch || 1.0,
          voiceSpeed: data.voice_speed || 1.0,
          llmModel: data.llm_model || 'gpt-4.1-mini',
          transcriptionService: data.transcription_service || 'azure',
          welcomeMessage: data.welcome_message || '',
          welcomeMessageDynamic: data.welcome_message_dynamic || false,
          welcomeMessageInterruptible: data.welcome_message_interruptible !== false,
          conversationalFlow: data.conversational_flow?.length > 0 
            ? data.conversational_flow 
            : (data.conversationalFlow?.length > 0 ? data.conversationalFlow : defaultAgentConfig.conversationalFlow),
          maxCallDuration: data.max_call_duration || 300,
          recordCalls: data.record_calls !== false,
          transferEnabled: data.enable_live_transfer || false,
          transferNumber: data.transfer_number || '',
          transferTriggerPhrases: data.transfer_trigger_phrases || defaultAgentConfig.transferTriggerPhrases,
          transferMessage: data.transfer_message || defaultAgentConfig.transferMessage,
          voicemailEnabled: data.voicemail_enabled || false,
          voicemailMessage: data.voicemail_message || '',
          assignedPhoneNumber: data.assigned_phone_number || '',
          knowledgeBase: data.knowledge_base || [],
          knowledgeBaseSize: data.knowledge_base_size || 0,
          integrations: data.integrations || [],
          postCallWebhook: data.post_call_webhook || '',
          postCallSummary: data.post_call_summary !== false,
          postCallTranscript: data.post_call_transcript !== false,
          postCallEmail: data.post_call_email || '',
          status: data.status || 'draft'
        });
      }
    } catch (error) {
      console.error('Error fetching agent:', error);
    } finally {
      setLoading(false);
    }
  }, [agentId, clientUser?.id, isNewAgent]);

  useEffect(() => {
    fetchAgent();
  }, [fetchAgent]);

  // Feature & Limit Helpers
  const hasFeature = (featureName) => {
    if (!clientSettings?.features) return true;
    return clientSettings.features[featureName] !== false;
  };

  const getLimit = (limitName) => {
    if (!clientSettings?.limits) return null;
    return clientSettings.limits[limitName];
  };

  const getVoiceSetting = (settingName) => {
    if (!clientSettings?.voice_settings) return null;
    return clientSettings.voice_settings[settingName];
  };

  const isVoiceAllowed = (voiceId) => {
    const allowedVoices = getVoiceSetting('allowed_voices');
    if (!allowedVoices || allowedVoices.length === 0) return true;
    return allowedVoices.includes(voiceId);
  };

  // Build save payload
  const buildSavePayload = useCallback(() => {
    // Build script from conversational flow sections that have actual content
    const activeSections = (agent.conversationalFlow || [])
      .filter(section => section.enabled && section.content?.trim());
    
    const scriptFromFlow = activeSections
      .map(section => `### ${section.title}\n${section.content}`)
      .join('\n\n');
    
    // Only include conversational_flow if sections have content
    const flowToSave = activeSections.length > 0 ? agent.conversationalFlow : [];
    
    return {
      name: agent.name,
      type: agent.type,
      language: agent.languages?.[0] || 'English',
      languages: agent.languages,
      voice_type: agent.voiceGender || 'female',
      voice_provider: agent.voiceProvider,
      voice_id: agent.voiceId,
      voice_gender: agent.voiceGender,
      voice_pitch: agent.voicePitch,
      voice_speed: agent.voiceSpeed,
      llm_model: agent.llmModel,
      transcription_service: agent.transcriptionService,
      script: scriptFromFlow || agent.welcomeMessage || 'AI voice assistant script',
      greeting: agent.welcomeMessage || 'Hello! How can I help you today?',
      welcome_message: agent.welcomeMessage,
      welcome_message_dynamic: agent.welcomeMessageDynamic,
      welcome_message_interruptible: agent.welcomeMessageInterruptible,
      conversational_flow: flowToSave,
      max_call_duration: agent.maxCallDuration,
      record_calls: agent.recordCalls,
      enable_live_transfer: agent.transferEnabled,
      transfer_number: agent.transferNumber,
      transfer_trigger_phrases: agent.transferTriggerPhrases,
      transfer_message: agent.transferMessage,
      voicemail_enabled: agent.voicemailEnabled,
      voicemail_message: agent.voicemailMessage,
      knowledge_base: agent.knowledgeBase,
      integrations: agent.integrations,
      post_call_webhook: agent.postCallWebhook,
      post_call_summary: agent.postCallSummary,
      post_call_transcript: agent.postCallTranscript,
      post_call_email: agent.postCallEmail,
      status: 'active',
      owner_id: clientUser?.id
    };
  }, [agent, clientUser?.id]);

  // Auto-save function (for existing agents only)
  const performAutoSave = useCallback(async () => {
    if (isNewAgent || !agent.name || saving || !clientUser?.id) return;
    
    setSaveStatus('saving');
    try {
      const payload = buildSavePayload();
      await clientAPI.updateAgent(clientUser?.id, agentId, payload);
      setSaveStatus('saved');
      setLastSaved(new Date());
    } catch (error) {
      console.error('Auto-save error:', error);
      setSaveStatus('error');
    }
  }, [isNewAgent, agent.name, saving, agentId, clientUser?.id, buildSavePayload]);

  // Auto-save effect - triggers on agent changes
  useEffect(() => {
    // Skip auto-save for new agents or during initial load
    if (isNewAgent || initialLoadRef.current || !autoSaveEnabled) {
      return;
    }
    
    // Mark as unsaved when agent changes
    setSaveStatus('unsaved');
    
    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Set new timer for auto-save
    autoSaveTimerRef.current = setTimeout(() => {
      performAutoSave();
    }, AUTO_SAVE_DELAY);
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [agent, isNewAgent, autoSaveEnabled, performAutoSave]);

  // Mark initial load complete after agent is fetched
  useEffect(() => {
    if (!loading && !isNewAgent) {
      // Small delay to ensure state is settled
      const timer = setTimeout(() => {
        initialLoadRef.current = false;
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, isNewAgent]);

  // Save Agent (manual save)
  const handleSave = async (showNotification = true) => {
    if (!agent.name) {
      setNotification({ type: 'error', message: 'Please enter an agent name' });
      return;
    }
    
    // Clear any pending auto-save
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    setSaving(true);
    setSaveStatus('saving');
    try {
      const payload = buildSavePayload();

      if (isNewAgent) {
        const response = await clientAPI.createAgent(clientUser?.id, payload);
        if (response.data.success) {
          if (showNotification) {
            setNotification({ type: 'success', message: 'Agent created successfully!' });
          }
          setTimeout(() => {
            navigate(`/client/agents/${response.data.agent.id}`);
          }, 1000);
        }
      } else {
        await clientAPI.updateAgent(clientUser?.id, agentId, payload);
        if (showNotification) {
          setNotification({ type: 'success', message: 'Changes saved successfully!' });
        }
      }
      
      setSaveStatus('saved');
      setLastSaved(new Date());
      setShowDeployDropdown(false);
    } catch (error) {
      console.error('Error saving agent:', error);
      setSaveStatus('error');
      const errorMessage = error.response?.data?.detail;
      const displayMessage = typeof errorMessage === 'string' 
        ? errorMessage 
        : Array.isArray(errorMessage) 
          ? errorMessage.map(e => e.msg).join(', ')
          : 'Failed to save agent. Please try again.';
      if (showNotification) {
        setNotification({ type: 'error', message: displayMessage });
      }
    } finally {
      setSaving(false);
    }
  };

  // Conversational Flow Handlers
  const addFlowSection = () => {
    const newSection = {
      id: Date.now().toString(),
      title: `Section ${agent.conversationalFlow.length + 1}`,
      enabled: true,
      content: ''
    };
    setAgent(prev => ({
      ...prev,
      conversationalFlow: [...prev.conversationalFlow, newSection]
    }));
  };

  const updateFlowSection = (id, updates) => {
    setAgent(prev => ({
      ...prev,
      conversationalFlow: prev.conversationalFlow.map(section =>
        section.id === id ? { ...section, ...updates } : section
      )
    }));
  };

  const deleteFlowSection = (id) => {
    if (agent.conversationalFlow.length <= 1) return;
    setAgent(prev => ({
      ...prev,
      conversationalFlow: prev.conversationalFlow.filter(section => section.id !== id)
    }));
  };

  // Phone Number Handlers
  const assignPhoneNumber = async (phoneNumber) => {
    try {
      const response = await clientAPI.assignPhoneToAgent(clientUser?.id, agentId, phoneNumber);
      if (response.data.success) {
        setAgent(prev => ({ ...prev, assignedPhoneNumber: phoneNumber }));
      }
    } catch (error) {
      console.error('Error assigning phone number:', error);
    }
  };

  const unassignPhoneNumber = async () => {
    try {
      const response = await clientAPI.unassignPhoneFromAgent(clientUser?.id, agentId);
      if (response.data.success) {
        setAgent(prev => ({ ...prev, assignedPhoneNumber: '' }));
      }
    } catch (error) {
      console.error('Error unassigning phone number:', error);
    }
  };

  // Knowledge Base Handlers
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploadingFiles(true);
    
    for (const file of files) {
      const newFile = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        name: file.name,
        size: file.size,
        type: file.type,
        status: 'processing'
      };
      
      setAgent(prev => ({
        ...prev,
        knowledgeBase: [...prev.knowledgeBase, newFile],
        knowledgeBaseSize: prev.knowledgeBaseSize + file.size
      }));

      // Simulate processing delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setAgent(prev => ({
        ...prev,
        knowledgeBase: prev.knowledgeBase.map(f => 
          f.id === newFile.id ? { ...f, status: 'ready' } : f
        )
      }));
    }
    
    setUploadingFiles(false);
    e.target.value = '';
  };

  const removeKnowledgeFile = (fileId) => {
    const file = agent.knowledgeBase.find(f => f.id === fileId);
    if (file) {
      setAgent(prev => ({
        ...prev,
        knowledgeBase: prev.knowledgeBase.filter(f => f.id !== fileId),
        knowledgeBaseSize: Math.max(0, prev.knowledgeBaseSize - file.size)
      }));
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="agent-editor-page">
      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-5 py-3 rounded-lg shadow-lg border ${
          notification.type === 'success' 
            ? 'bg-green-500/20 border-green-500/40 text-green-400' 
            : 'bg-red-500/20 border-red-500/40 text-red-400'
        }`}>
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="font-medium">{notification.message}</span>
          <button 
            onClick={() => setNotification(null)}
            className="ml-2 opacity-70 hover:opacity-100"
          >
            ×
          </button>
        </div>
      )}

      <AgentHeader 
        agent={agent}
        setAgent={setAgent}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        viewMode={viewMode}
        setViewMode={setViewMode}
        showTestDropdown={showTestDropdown}
        setShowTestDropdown={setShowTestDropdown}
        showDeployDropdown={showDeployDropdown}
        setShowDeployDropdown={setShowDeployDropdown}
        onSave={handleSave}
        saving={saving}
        saveStatus={saveStatus}
        lastSaved={lastSaved}
        autoSaveEnabled={autoSaveEnabled}
        setAutoSaveEnabled={setAutoSaveEnabled}
      />

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {activeTab === 'details' && (
          <DetailsTab 
            agent={agent}
            setAgent={setAgent}
            hasFeature={hasFeature}
            getVoiceSetting={getVoiceSetting}
            isVoiceAllowed={isVoiceAllowed}
            addFlowSection={addFlowSection}
            updateFlowSection={updateFlowSection}
            deleteFlowSection={deleteFlowSection}
          />
        )}

        {activeTab === 'call' && (
          <CallConfigTab 
            agent={agent}
            setAgent={setAgent}
            agentId={agentId}
            hasFeature={hasFeature}
            assignPhoneNumber={assignPhoneNumber}
            unassignPhoneNumber={unassignPhoneNumber}
          />
        )}

        {activeTab === 'knowledge' && (
          <KnowledgeBaseTab 
            agent={agent}
            setAgent={setAgent}
            agentId={agentId}
            hasFeature={hasFeature}
            getLimit={getLimit}
            handleFileUpload={handleFileUpload}
            removeKnowledgeFile={removeKnowledgeFile}
            uploadingFiles={uploadingFiles}
          />
        )}

        {activeTab === 'integrations' && (
          <IntegrationsTab 
            agent={agent}
            setAgent={setAgent}
            hasFeature={hasFeature}
          />
        )}

        {activeTab === 'postcall' && (
          <PostCallTab 
            agent={agent}
            setAgent={setAgent}
          />
        )}

        {activeTab === 'calls' && (
          <RecentCallsTab 
            agent={agent}
          />
        )}

        {activeTab === 'history' && (
          <VersionHistoryTab
            agentId={!isNewAgent ? agentId : null}
            clientId={clientUser?.id}
            onRestore={(restoredAgent) => {
              setAgent(prev => ({ ...prev, ...restoredAgent }));
              setNotification({ type: 'success', message: 'Agent restored to previous version' });
            }}
          />
        )}
      </div>
    </div>
  );
};

export default AgentEditor;
